import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/Combobox';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { SelectField } from '../../components/ui/SelectField';
import { ApiError } from '../../lib/api';
import type { LiveAd, LiveAdSet, LiveCampaignDetail, LiveCampaignRow, LiveStatusFilter } from '../../types/metaLiveCampaigns';
import { createDraft, getEligibleAccounts, getLiveAd, getLiveCampaignDetail, getLiveCampaigns } from './api';
import { buildAttachDraftInput, destinationLabel, firstLine, NOT_READABLE_NOTE, withMediaMode } from './liveAdSetFlow';
import { DuplicateCampaignDialog } from './components/DuplicateCampaignDialog';
import { useDebouncedValue } from './useDebouncedValue';

// "Add an ad to a live ad set": pick a campaign, an ad set, then an ad to start from
// (or blank). Everything here is READ from Meta via Zernio; nothing changes until the
// composed ad is published, and then only one ad is added.

const fmtDate = (iso: string | null) => (iso ? iso.slice(0, 10) : '—');

export function CampaignStep({
  accountId,
  onPick,
  rowActions,
}: {
  accountId: string;
  onPick: (c: LiveCampaignRow) => void;
  rowActions?: (c: LiveCampaignRow) => React.ReactNode;
}) {
  const [status, setStatus] = useState<LiveStatusFilter>('live');
  const [kyckOnly, setKyckOnly] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [status, kyckOnly, search, accountId]);
  const [pastedId, setPastedId] = useState('');
  const pasted = pastedId.trim();

  const q = useQuery({
    queryKey: ['metaLiveCampaigns', accountId, status, kyckOnly, search, page],
    queryFn: () => getLiveCampaigns(accountId, { status, search, publishedByKyck: kyckOnly, page }),
  });

  return (
    <section aria-label="Pick a campaign" className="space-y-3">
      <h2 className="text-base font-semibold text-slate-900">1. Pick a campaign</h2>
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by campaign name…"
          aria-label="Search campaigns"
          className="w-64 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
        />
        <SelectField
          value={status}
          onValueChange={(v) => setStatus(v as LiveStatusFilter)}
          size="sm"
          className="w-32"
          aria-label="Campaign status"
          options={[
            { value: 'live', label: 'Live' },
            { value: 'all', label: 'All' },
          ]}
        />
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={kyckOnly} onChange={(e) => setKyckOnly(e.target.checked)} />
          Published by Kyck for this account
        </label>
      </div>
      <p className="text-xs text-slate-500">
        Ad accounts are often shared between locations. Search for the club, or tick "Published by Kyck".
      </p>

      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <span>Can't see it? Paste the campaign ID:</span>
        <input
          type="text"
          inputMode="numeric"
          value={pastedId}
          onChange={(e) => setPastedId(e.target.value)}
          placeholder="Meta campaign ID"
          aria-label="Campaign ID"
          className="w-52 rounded-md border border-slate-300 px-2 py-1 text-sm"
        />
        <Button
          variant="outline"
          size="sm"
          disabled={!/^\d{5,25}$/.test(pasted)}
          onClick={() =>
            onPick({
              id: pasted,
              name: `Campaign ${pasted}`,
              objective: null,
              metaStatus: null,
              isLive: false,
              firstRan: null,
              lastRan: null,
              spend30d: null,
              publishedByKyck: false,
            })
          }
        >
          Load it
        </Button>
      </div>

      {q.isLoading && <CenteredSpinner />}
      {q.error && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {q.error instanceof ApiError ? q.error.message : 'Could not load campaigns.'}
        </p>
      )}
      {q.data && (
        <>
          {q.data.truncated && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              This ad account is large; the list may be incomplete. Use the search box.
            </p>
          )}
          {q.data.campaigns.length === 0 ? (
            <p className="text-sm text-slate-500">No campaigns match.</p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
              {q.data.campaigns.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center">
                  <button
                    type="button"
                    onClick={() => onPick(c)}
                    className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-slate-50"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-slate-900">{c.name}</span>
                      <span className="block text-xs text-slate-500">
                        {c.objective ?? '—'} · first ran {fmtDate(c.firstRan)} · last ran {fmtDate(c.lastRan)}
                        {c.spend30d != null ? ` · ${Math.round(c.spend30d)} spent in the last 30 days` : ''}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      {c.publishedByKyck && <Badge variant="info">Kyck</Badge>}
                      <Badge variant={c.isLive ? 'success' : 'neutral'}>{c.isLive ? 'Live' : (c.metaStatus ?? 'Unknown')}</Badge>
                    </span>
                  </button>
                  {rowActions && <span className="flex flex-wrap gap-2 px-3 py-1.5">{rowActions(c)}</span>}
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>
              {q.data.total} campaign{q.data.total === 1 ? '' : 's'} · page {q.data.page} of {q.data.totalPages}
            </span>
            <span className="flex gap-2">
              <Button variant="outline" size="sm" disabled={q.data.page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled={q.data.page >= q.data.totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </span>
          </div>
        </>
      )}
    </section>
  );
}

function AdSetStep({ detail, onPick, onBack }: { detail: LiveCampaignDetail; onPick: (s: LiveAdSet) => void; onBack: () => void }) {
  return (
    <section aria-label="Pick an ad set" className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">2. Pick an ad set in "{detail.name}"</h2>
        <Button variant="outline" size="sm" onClick={onBack}>
          Change campaign
        </Button>
      </div>
      {detail.adSets.length === 0 ? (
        <p className="text-sm text-slate-500">This campaign has no ad sets we can read.</p>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
          {detail.adSets.map((s) => (
            <li key={s.id}>
              <button type="button" onClick={() => onPick(s)} className="flex w-full flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-slate-50">
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-slate-900">{s.name || s.id}</span>
                  <span className="block text-xs text-slate-500">
                    {s.budget ? `${s.budget.amount} ${s.budget.type}` : 'Budget set on the campaign or not readable'} · {s.ads.length} ad{s.ads.length === 1 ? '' : 's'}
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <Badge variant={s.destination === 'unknown' ? 'warning' : 'neutral'}>{destinationLabel(s.destination)}</Badge>
                  <Badge variant={s.metaStatus?.toLowerCase() === 'active' ? 'success' : 'neutral'}>{s.metaStatus ?? 'Unknown'}</Badge>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AdStep({
  adSet,
  adSets,
  busy,
  onPick,
  onReuseExact,
  onBack,
}: {
  adSet: LiveAdSet;
  adSets: LiveAdSet[];
  busy: boolean;
  onPick: (a: LiveAd | null) => void;
  onReuseExact: (a: LiveAd, into: LiveAdSet) => void;
  onBack: () => void;
}) {
  const [intoId, setIntoId] = useState(adSet.id);
  const into = adSets.find((s) => s.id === intoId) ?? adSet;
  return (
    <section aria-label="Start from an ad" className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900">3. Start from an ad in "{adSet.name}"</h2>
        <Button variant="outline" size="sm" onClick={onBack} disabled={busy}>
          Change ad set
        </Button>
      </div>
      <p className="text-xs text-slate-500">
        The new ad starts from the ad's text, link and Page / Instagram identity. You then choose the original image or video, or add a
        new one. "Reuse this exact ad" attaches the same creative, unchanged, to the ad set chosen below (same ad account and Page only).
      </p>
      {adSets.length > 1 && (
        <label className="flex max-w-md items-center gap-2 text-xs text-slate-600">
          Reuse into
          <select
            aria-label="Reuse into ad set"
            value={intoId}
            onChange={(e) => setIntoId(e.target.value)}
            className="flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm"
          >
            {adSets.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name || s.id}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {adSet.ads.map((a) => (
          <div key={a.id} className="space-y-1.5">
          <button
            type="button"
            disabled={busy}
            onClick={() => onPick(a)}
            className="flex w-full gap-3 rounded-md border border-slate-200 p-3 text-left hover:bg-slate-50 disabled:opacity-60"
          >
            {a.thumbnailUrl ? (
              <img src={a.thumbnailUrl} alt="" className="h-16 w-16 shrink-0 rounded object-cover" />
            ) : (
              <div className="h-16 w-16 shrink-0 rounded bg-slate-100" aria-hidden="true" />
            )}
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-slate-900">{a.name || a.id}</span>
              <span className="block text-xs text-slate-500">{a.metaStatus ?? 'Unknown'}</span>
              <span className="mt-1 block text-xs text-slate-600">{firstLine(a.primaryText) || 'Text not readable from Meta'}</span>
            </span>
          </button>
          {a.creativeId && (
            <Button variant="outline" size="sm" className="w-full" disabled={busy} onClick={() => onReuseExact(a, into)}>
              Reuse this exact ad{into.id !== adSet.id ? ` in "${into.name || into.id}"` : ''}
            </Button>
          )}
          </div>
        ))}
        <button
          type="button"
          disabled={busy}
          onClick={() => onPick(null)}
          className="flex items-center justify-center rounded-md border border-dashed border-slate-300 p-3 text-sm font-medium text-kyck-700 hover:bg-slate-50 disabled:opacity-60"
        >
          Start blank
        </button>
      </div>
      <p className="text-xs text-slate-500">
        Anything Meta does not return for the ad you pick is left empty ({NOT_READABLE_NOTE}).
      </p>
      {busy && <p className="text-xs text-slate-500">Creating your draft…</p>}
    </section>
  );
}

export function AddAdToLiveAdSetPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const accountId = params.get('account') ?? '';
  const eligible = useQuery({ queryKey: ['metaAdEligibleAccounts'], queryFn: getEligibleAccounts });

  const [campaign, setCampaign] = useState<LiveCampaignRow | null>(null);
  const [adSet, setAdSet] = useState<LiveAdSet | null>(null);
  const [duplicating, setDuplicating] = useState<LiveCampaignRow | null>(null);

  const setAccount = (id: string) => {
    setCampaign(null);
    setAdSet(null);
    setParams(id ? { account: id } : {}, { replace: true });
  };

  const detail = useQuery({
    queryKey: ['metaLiveCampaign', accountId, campaign?.id],
    queryFn: () => getLiveCampaignDetail(accountId, campaign!.id),
    enabled: Boolean(accountId && campaign),
  });

  const create = useMutation({
    mutationFn: async (args: { picked: LiveAd | null; exactInto?: LiveAdSet }) => {
      const { picked, exactInto } = args;
      // The one getAd call: only for the ad the AM picked; the server decides whether it is needed.
      const source = picked ? await getLiveAd(accountId, detail.data!.id, picked.id).catch(() => picked) : null;
      const base = buildAttachDraftInput({ accountId, campaign: detail.data!, adSet: exactInto ?? adSet!, source });
      return createDraft(exactInto ? withMediaMode(base, 'exact') : base);
    },
    onSuccess: ({ draft }) => navigate(`/advertising/meta/compose/${draft.id}`),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not create the draft.'),
  });

  const options = useMemo(
    () => (eligible.data?.accounts ?? []).map((a) => ({ value: a.id, label: a.name })),
    [eligible.data],
  );

  return (
    <div>
      <PageHeader
        title="Add an ad to a live ad set"
        description="Add one new image or video to an ad set that is already running. Nothing else on the campaign changes."
        backHref="/advertising/meta"
      />
      <div className="max-w-6xl space-y-8">
        <div className="max-w-md">
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Account</label>
          <Combobox
            aria-label="Account"
            searchPlaceholder="Search accounts…"
            value={accountId}
            onValueChange={setAccount}
            options={[{ value: '', label: 'Choose an account…' }, ...options]}
          />
        </div>

        {accountId && !campaign && (
          <CampaignStep
            accountId={accountId}
            onPick={setCampaign}
            rowActions={(c) => (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/advertising/meta/express-images?account=${encodeURIComponent(accountId)}&campaign=${encodeURIComponent(c.id)}`)}
                >
                  Add images (express)
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/advertising/meta/clone?account=${encodeURIComponent(accountId)}&campaign=${encodeURIComponent(c.id)}`)}
                >
                  Clone to a new campaign
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDuplicating(c)}>
                  Duplicate exactly (paused)
                </Button>
              </>
            )}
          />
        )}

        {duplicating && <DuplicateCampaignDialog accountId={accountId} campaign={duplicating} onClose={() => setDuplicating(null)} />}

        {accountId && campaign && !adSet && (
          <>
            {detail.isLoading && <CenteredSpinner />}
            {detail.error && (
              <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                {detail.error instanceof ApiError ? detail.error.message : 'Could not load that campaign.'}{' '}
                <button type="button" className="underline" onClick={() => setCampaign(null)}>
                  Pick another
                </button>
              </p>
            )}
            {detail.data && <AdSetStep detail={detail.data} onPick={setAdSet} onBack={() => setCampaign(null)} />}
          </>
        )}

        {accountId && campaign && adSet && detail.data && (
          <AdStep
            adSet={adSet}
            adSets={detail.data.adSets}
            busy={create.isPending}
            onPick={(a) => create.mutate({ picked: a })}
            onReuseExact={(a, into) => create.mutate({ picked: a, exactInto: into })}
            onBack={() => setAdSet(null)}
          />
        )}
      </div>
    </div>
  );
}
